import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutGrid, Table2, Music2, FileText, ShieldCheck, Bell, Calendar, Users, Mic2, Theater, Settings, LogOut, Receipt, UserCog, ScrollText, Trash2, FileSignature, AlertTriangle, Copy, FileDown, KeyRound, Archive, PanelLeftClose, PanelLeftOpen, UserCircle, Sparkles, Menu, X, BarChart3, Briefcase, CheckSquare, RefreshCw, Wallet } from 'lucide-react';
import logoCygnature from '@/assets/logo-cygnature.png';
import { useCrm, crmStore } from './store';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './auth/AuthContext';
import { usePermissions, PermModule } from './auth/PermissionsContext';
import StructureSelector from './components/StructureSelector';
import NotificationBell from './notifications/NotificationBell';
import DemoModeToggle from './components/DemoModeToggle';
import DemoModeBanner from './components/DemoModeBanner';

interface Props {
  onLogout: () => void;
}

const navItems: Array<{ to: string; label: string; icon: any; end?: boolean; badgeKey?: 'prestActives'|'propActives'|'signed'|'facturesEnAttente'|'relance'|'facturesImpayees'; danger?: boolean; module: PermModule; adminOnly?: boolean; hideForAdminDirection?: boolean }> = [
  // 1. Vue d'ensemble
  { to: '', label: 'Dashboard', icon: LayoutGrid, end: true, module: 'dashboard' },
  { to: 'stats-avancees', label: 'Stats avancées', icon: BarChart3, module: 'dashboard' },
  { to: 'mes-dossiers', label: 'Mes dossiers', icon: Briefcase, module: 'prestations', hideForAdminDirection: true },
  { to: 'taches', label: 'Tâches', icon: CheckSquare, module: 'prestations' },
  { to: 'suivi-reglement', label: 'Suivi règlements', icon: Wallet, module: 'facturation' },
  { to: 'agenda', label: 'Agenda', icon: Calendar, module: 'agenda' },

  // 2. Annuaires (préalables à toute prestation)
  { to: 'clients', label: 'Clients', icon: Users, module: 'clients' },
  { to: 'artistes', label: 'Artistes', icon: Mic2, module: 'artistes' },

  // 3. Cycle de vente (de la demande à l'encaissement)
  { to: 'nouvelle-demande', label: 'Nouvelle demande', icon: Sparkles, module: 'prestations' },
  { to: 'prestations', label: 'Prestations', icon: Table2, badgeKey: 'prestActives', module: 'prestations' },
  { to: 'propositions-contrats', label: 'Propositions & Contrats', icon: FileSignature, badgeKey: 'propActives', module: 'propositions' },
  { to: 'contrats-artistes', label: 'Contrats artistes', icon: Mic2, module: 'contrats' },
  { to: 'facturation', label: 'Facturation', icon: Receipt, badgeKey: 'facturesEnAttente', module: 'facturation' },

  // 4. Analyse / suivi
  { to: 'annuaire-analytique', label: 'Annuaire analytique', icon: Users, module: 'suivi' },

  // 5. Maintenance / archives
  { to: 'doublons', label: 'Doublons', icon: Copy, module: 'doublons' },
  { to: 'archives', label: 'Archives', icon: Archive, module: 'prestations' },
  { to: 'corbeille', label: 'Corbeille', icon: Trash2, module: 'corbeille' },

  // 6. Administration
  { to: 'parametres', label: 'Paramètres', icon: Settings, module: 'parametres' },
  { to: 'roles-permissions', label: 'Rôles & permissions', icon: KeyRound, module: 'utilisateurs', adminOnly: true },
  { to: 'utilisateurs', label: 'Utilisateurs', icon: UserCog, module: 'utilisateurs', adminOnly: true },
];

export default function CrmLayout({ children, onLogout }: { children: React.ReactNode } & Props) {
  const data = useCrm();
  const { hasRole, user } = useAuth();
  const { can } = usePermissions();
  const isAdmin = hasRole('admin');
  const isAdminOrDirection = isAdmin || hasRole('direction');
  const visibleNav = navItems.filter((it) => {
    if (it.adminOnly && !isAdmin) return false;
    if (it.hideForAdminDirection && isAdminOrDirection) return false;
    return it.adminOnly ? isAdmin : can(it.module, 'view');
  });

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('crm-sidebar-collapsed') === '1';
  });
  useEffect(() => {
    localStorage.setItem('crm-sidebar-collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  // Mobile drawer
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Auto-archivage des prestations dont la date d'événement est dépassée
  // (Réalisée si Signé, Expirée si en cours, Brouillon abandonné, etc.)
  // Lancé une fois par jour côté client à l'ouverture du CRM.
  useEffect(() => {
    const KEY = 'crm-last-auto-archive';
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(KEY) === today) return;
    (async () => {
      try {
        await crmStore.runAutoArchive();
        localStorage.setItem(KEY, today);
      } catch (e) {
        console.warn('[auto-archive] échec silencieux', e);
      }
    })();
  }, []);

  // Compteur propositions actives (hors Brouillon/Refusée/Expirée), MAJ live via realtime
  const [propActivesCount, setPropActivesCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchProp = async () => {
      const { count } = await supabase
        .from('propositions')
        .select('id', { count: 'exact', head: true })
        .in('statut', ['Envoyée', 'Acceptée']);
      if (!cancelled) setPropActivesCount(count || 0);
    };
    fetchProp();
    const channel = supabase
      .channel('prop-count')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'propositions' }, fetchProp)
      .subscribe();
    const onVis = () => { if (document.visibilityState === 'visible') fetchProp(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', fetchProp);
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', fetchProp);
    };
  }, []);

  // Statuts "actifs" pour les prestations (hors terminaux : Signé, Refusée, Réalisée, Expirée, Annulée, Projet en pause)
  const PREST_ACTIVES = ['Demande de Rdv','Propal A FAIRE','Propal transmise','Relance','Relance Tel, info transmise','Contrat Transmis'];

  const counts = {
    prestActives: data.prest.filter((p) => !p.archivedAt && PREST_ACTIVES.includes(p.statut)).length,
    propActives: propActivesCount,
    signed: data.prest.filter((p) => p.statut === 'Signé').length,
    relance: data.prest.filter((p) => /relance|propal/i.test(p.statut) || p.statut === 'Demande de Rdv').length,
    facturesEnAttente: (data.factures || []).filter((f) => ['Émise', 'Envoyée', 'Partiellement payée'].includes(f.statut)).length,
    facturesImpayees: (data.factures || []).filter((f) => {
      if (['Payée', 'Annulée', 'Brouillon'].includes(f.statut)) return false;
      const ttc = f.lignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT * (1 + l.tvaPct / 100), 0) * (1 - (f.remisePct || 0) / 100);
      return Math.max(0, ttc - (f.montantPaye || 0)) > 0.01 && f.dateEcheance && new Date(f.dateEcheance) < new Date();
    }).length,
  };

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });

  // Sidebar desktop only ; sur mobile c'est un drawer
  const sidebarWidth = collapsed ? 'lg:w-16' : 'lg:w-64';
  const mainMargin = collapsed ? 'lg:ml-16' : 'lg:ml-64';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-body">
      {/* Topbar mobile */}
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between gap-3 px-4 h-14 bg-slate-900 border-b border-slate-800">
        <button
          onClick={() => setMobileOpen(true)}
          aria-label="Ouvrir le menu"
          className="w-9 h-9 rounded-lg bg-slate-800/80 hover:bg-slate-800 flex items-center justify-center text-slate-200"
        >
          <Menu size={18} strokeWidth={1.5} />
        </button>
        <div className="flex items-center gap-2 min-w-0 flex-1 justify-center">
          <img src={logoCygnature} alt="Cygnature" className="h-7 w-auto" />
          <span className="font-heading text-amber-400 text-base tracking-wide truncate">CYGNATURE</span>
        </div>
        <button
          onClick={() => window.location.reload()}
          aria-label="Rafraîchir la page"
          title="Rafraîchir la page"
          className="p-2 rounded-lg text-slate-400 hover:text-amber-300 hover:bg-slate-800/60 transition-colors"
        >
          <RefreshCw size={16} strokeWidth={1.5} />
        </button>
        <NotificationBell />
      </header>
      {/* Sélecteur structure mobile (sous le header) */}
      <div className="lg:hidden sticky top-14 z-30 px-3 py-2 bg-slate-950/95 backdrop-blur border-b border-slate-800 flex justify-center">
        <StructureSelector compact />
      </div>

      {/* Overlay mobile */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${sidebarWidth} h-[100dvh] bg-slate-900 border-r border-slate-800 flex flex-col fixed left-0 top-0 z-50 overflow-y-auto transition-transform lg:transition-all duration-200
            w-72 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        >
          {/* Logo + collapse */}
          <div className={`${collapsed ? 'lg:px-2 lg:py-4 lg:justify-center' : 'lg:px-6 lg:py-6'} px-6 py-6 border-b border-slate-800 flex items-center gap-3 relative`}>
            <img src={logoCygnature} alt="Cygnature" className={collapsed ? 'lg:h-8 lg:mx-auto h-12 w-auto' : 'h-12 w-auto'} />
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="font-heading text-amber-400 text-lg leading-none tracking-wide">CYGNATURE</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-1">CRM Événementiel</div>
              </div>
            )}
            {/* Bouton fermer (mobile) */}
            <button
              onClick={() => setMobileOpen(false)}
              aria-label="Fermer le menu"
              className="lg:hidden absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-800 border border-slate-700 hover:bg-amber-500 hover:text-slate-950 text-slate-300 flex items-center justify-center"
            >
              <X size={14} strokeWidth={2} />
            </button>
            {/* Bouton collapse (desktop) */}
            <button
              onClick={() => setCollapsed((v) => !v)}
              title={collapsed ? 'Déployer le menu' : 'Réduire le menu'}
              className="hidden lg:flex absolute bottom-1.5 right-1.5 w-6 h-6 rounded-full bg-slate-800 border border-slate-700 hover:bg-amber-500 hover:text-slate-950 text-slate-300 items-center justify-center shadow-md transition-colors"
            >
              {collapsed ? <PanelLeftOpen size={11} strokeWidth={2} /> : <PanelLeftClose size={11} strokeWidth={2} />}
            </button>
          </div>

          {/* Toggle Mode Démo / Réel */}
          <div className={`${collapsed ? 'lg:px-2' : 'lg:px-3'} px-3 pt-3`}>
            <DemoModeToggle collapsed={collapsed} />
          </div>

          {/* Nav */}
          <nav className={`flex-1 ${collapsed ? 'lg:px-2' : 'lg:px-3'} px-3 py-4 space-y-1 overflow-y-auto`}>
            {visibleNav.map((it) => {
              const Icon = it.icon;
              const badgeVal = it.badgeKey ? counts[it.badgeKey] : null;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  title={collapsed ? it.label : undefined}
                  className={({ isActive }) =>
                    `flex items-center ${collapsed ? 'lg:justify-center lg:px-2 justify-between px-4' : 'justify-between px-4'} gap-3 py-2.5 rounded-lg text-sm transition-colors relative ${
                      isActive ? 'bg-amber-500/10 text-amber-400 font-medium' : 'text-slate-300 hover:bg-slate-800/60 hover:text-slate-100'
                    }`
                  }
                >
                  <span className={`flex items-center gap-3 ${collapsed ? 'lg:gap-0' : ''}`}>
                    <Icon size={16} strokeWidth={1.5} />
                    <span className={collapsed ? 'lg:hidden' : ''}>{it.label}</span>
                  </span>
                  {badgeVal != null && badgeVal > 0 && (
                    <>
                      <span className={`${collapsed ? 'lg:flex hidden' : 'hidden'} absolute top-0.5 right-0.5 text-[8px] font-bold w-4 h-4 items-center justify-center rounded-full ${it.danger ? 'bg-red-500/30 text-red-300' : 'bg-emerald-500/30 text-emerald-300'}`}>
                        {badgeVal > 99 ? '∞' : badgeVal}
                      </span>
                      <span className={`${collapsed ? 'lg:hidden' : ''} text-[10px] font-bold px-2 py-0.5 rounded-full ${it.danger ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                        {badgeVal}
                      </span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Footer actions */}
          <div className={`${collapsed ? 'lg:p-2' : 'lg:p-3'} p-3 border-t border-slate-800 space-y-2`}>
            <NavLink
              to="mon-compte"
              title="Mon compte"
              className={({ isActive }) =>
                `w-full flex items-center justify-center gap-2 ${collapsed ? 'lg:px-2 px-3' : 'px-3'} py-2 rounded-lg text-xs transition-colors ${
                  isActive ? 'bg-amber-500/10 text-amber-400' : 'bg-slate-800/40 hover:bg-slate-800 text-slate-300'
                }`
              }
            >
              <UserCircle size={13} strokeWidth={1.5} />
              <span className={`truncate flex-1 text-left ${collapsed ? 'lg:hidden' : ''}`} title={user?.email || ''}>
                {user?.email || 'Mon compte'}
              </span>
            </NavLink>
            <button
              onClick={onLogout}
              title="Déconnexion"
              className={`w-full flex items-center justify-center gap-2 ${collapsed ? 'lg:px-2 px-3' : 'px-3'} py-2 bg-slate-800/60 hover:bg-slate-800 rounded-lg text-xs text-slate-300 transition-colors`}
            >
              <LogOut size={13} strokeWidth={1.5} />
              <span className={collapsed ? 'lg:hidden' : ''}>Déconnexion</span>
            </button>
            <div className={`text-[10px] text-slate-500 text-center pt-1 border-t border-slate-800/60 mt-2 ${collapsed ? 'lg:hidden' : ''}`}>{today}</div>
          </div>
        </aside>

        {/* Main */}
        <main className={`flex-1 ${mainMargin} min-h-screen transition-all duration-200 min-w-0`}>
          <DemoModeBanner />
          {/* Header sticky desktop avec sélecteur structure */}
          <div className="hidden lg:flex sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-3 bg-slate-950/90 backdrop-blur border-b border-slate-800/60 items-center justify-end gap-3">
            <button
              onClick={() => window.location.reload()}
              title="Rafraîchir la page"
              className="p-2 rounded-lg text-slate-400 hover:text-amber-300 hover:bg-slate-800/60 transition-colors"
            >
              <RefreshCw size={16} strokeWidth={1.5} />
            </button>
            <NotificationBell />
            <StructureSelector />
          </div>
          <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-[1600px] mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}
